import {RefObject, useRef} from 'react';

import type {
    OnShippingAddressChangeActions,
    OnShippingAddressChangeData,
    OnShippingOptionsChangeActions,
    OnShippingOptionsChangeData,
} from '@paypal/paypal-js/types';

import {callbackWithAnalyticsEvent} from 'libs/analytics/callbackWithAnalytic';
import {ShippingMethodType} from 'types/shippingMethodsType';

import {useShippingAddressChange} from './useShippingAddressChange';
import {useShippingOptionsChange} from './useShippingOptionsChange';

/**
 * Returns object with paypal v3 callback handlers
 */
export const usePayPalButtonHandlers = (
    sessionIdRef: RefObject<string>,
    placeOfInteraction: 'cart' | 'checkout',
) => {
    const addressChangeCounterRef = useRef(0);
    const shippingMethodsRef = useRef<ShippingMethodType[]>([]);
    const onShippingAddressChange = useShippingAddressChange();
    const onShippingOptionsChange = useShippingOptionsChange(shippingMethodsRef);

    return {
        onShippingAddressChange: async (
            addressFromPayPal: OnShippingAddressChangeData,
            action: OnShippingAddressChangeActions,
        ) => {
            await callbackWithAnalyticsEvent('shippingAddress changed', 'PayPal', async () => {
                addressChangeCounterRef.current += 1;
                shippingMethodsRef.current = await onShippingAddressChange(
                    addressFromPayPal,
                    action,
                    {
                        addressChangeCounter: addressChangeCounterRef.current,
                        id: sessionIdRef.current!,
                        placeOfInteraction,
                    },
                );
            });
        },
        onShippingOptionsChange: async (
            newOptionData: OnShippingOptionsChangeData,
            action: OnShippingOptionsChangeActions,
        ) => {
            await callbackWithAnalyticsEvent('shippingOptions changed', 'PayPal', () => {
                return onShippingOptionsChange(newOptionData, action, {
                    id: sessionIdRef.current!,
                });
            });
        },
    };
};
